import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Gamepad2, EyeOff, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { FreeToggle } from './FreeToggle';
import { SimulationForm, emptySim } from './simulations/SimulationForm';
import { SimCategoriesPanel } from './simulations/SimCategoriesPanel';
import { levelLabel, errText } from './simulations/simUtils';

const SimulationsManager = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => Promise.all([api.get('/admin/simulations'), api.get('/simulation-categories')])
    .then(([s, c]) => { setItems(s.data || []); setCats(c.data || []); }).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/simulations/${form.id}`, form); else await api.post('/simulations', form);
      toast.success(L('تم حفظ المحاكاة', 'Simulation saved'));
      setEditing(null); load();
    } catch (e) { toast.error(errText(e, L('تعذر الحفظ', 'Could not save'))); }
  };
  const remove = async (s) => {
    if (!window.confirm(L('حذف هذه المحاكاة؟', 'Delete this simulation?'))) return;
    try { await api.delete(`/simulations/${s.id}`); setItems(xs => xs.filter(x => x.id !== s.id)); toast.success(L('تم الحذف', 'Deleted')); }
    catch (e) { toast.error(errText(e, 'Error')); }
  };
  const toggleFree = async (s) => {
    try { const { data } = await api.patch(`/simulations/${s.id}/free`); setItems(xs => xs.map(x => (x.id === s.id ? { ...x, is_free: data.is_free } : x))); }
    catch (e) { toast.error(errText(e, 'Error')); }
  };
  const togglePublish = async (s) => {
    try { const { data } = await api.patch(`/simulations/${s.id}/publish`); setItems(xs => xs.map(x => (x.id === s.id ? { ...x, published: data.published } : x))); }
    catch (e) { toast.error(errText(e, 'Error')); }
  };
  const openEdit = async (s) => {
    try { const { data } = await api.get(`/admin/simulations/${s.id}`); setEditing({ ...emptySim(), ...data }); }
    catch (e) { toast.error(errText(e, 'Error')); }
  };
  const move = async (i, d) => {
    const next = [...items]; [next[i], next[i + d]] = [next[i + d], next[i]];
    setItems(next);
    try { await api.put('/simulations/reorder', { ids: next.map(x => x.id) }); } catch (e) { toast.error(errText(e, 'Error')); load(); }
  };
  const catName = (id) => cats.find(c => c.id === id)?.nameAr || L('بدون تصنيف', 'No category');

  return (
    <div data-testid="simulations-manager" className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-extrabold text-slate-900 text-lg">{L('إدارة المحاكاة التفاعلية', 'Manage simulations')}</h3>
          <p className="text-slate-500 text-sm" data-testid="sim-admin-count">{items.length} {L('محاكاة', 'simulations')}</p>
        </div>
        <button data-testid="sim-add-btn" onClick={() => setEditing(emptySim(cats[0]?.id))} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors"><Plus className="w-4 h-4" />{L('محاكاة جديدة', 'New simulation')}</button>
      </div>
      <SimCategoriesPanel categories={cats} sims={items} reload={load} />
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        : items.length === 0 ? <div className="text-center text-slate-500 py-10">{L('لا توجد محاكاة بعد', 'No simulations yet')}</div>
        : (
          <ul className="divide-y divide-slate-100">
            {items.map((s, i) => (
              <li key={s.id} data-testid={`admin-sim-row-${s.id}`} className="flex flex-wrap items-center gap-3 py-3">
                <div className="flex flex-col">
                  <button data-testid={`admin-sim-up-${s.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                  <button data-testid={`admin-sim-down-${s.id}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
                </div>
                {s.image ? <img src={s.image} alt="" className="w-16 h-10 object-cover rounded-lg" /> : <Gamepad2 className="w-5 h-5 text-blue-500" />}
                <div className="flex-1 min-w-[180px]">
                  <div className="font-bold text-slate-900 text-sm flex flex-wrap items-center gap-2">{s.titleAr}
                    {!s.has_html && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{L('بدون كود', 'No code')}</span>}
                  </div>
                  <div className="text-xs text-slate-400">{catName(s.category_id)} • {levelLabel(s.level, 'ar')} • {s.questions_count || 0} {L('سؤال', 'questions')}</div>
                </div>
                <button data-testid={`admin-sim-publish-${s.id}`} onClick={() => togglePublish(s)}
                  className={`h-8 px-3 rounded-full text-xs font-extrabold border inline-flex items-center gap-1 ${s.published ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-600'}`}>
                  {s.published ? <><Eye className="w-3.5 h-3.5" />{L('منشورة', 'Published')}</> : <><EyeOff className="w-3.5 h-3.5" />{L('مسودة', 'Draft')}</>}
                </button>
                <FreeToggle value={s.is_free} onChange={() => toggleFree(s)} testId={`admin-sim-free-${s.id}`} />
                <button data-testid={`admin-sim-edit-${s.id}`} onClick={() => openEdit(s)} className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`admin-sim-delete-${s.id}`} onClick={() => remove(s)} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent dir="rtl" className="max-w-6xl w-[96vw] bg-white">
          <DialogHeader>
            <DialogTitle>{editing?.id ? L('تعديل المحاكاة', 'Edit simulation') : L('محاكاة جديدة', 'New simulation')}</DialogTitle>
            <DialogDescription>{L('ألصق كود HTML، أضف الأسئلة والتعليمات، جرّبها في «معاينة» ثم احفظها كمسودة أو انشرها.', 'Paste the HTML, add questions and instructions, try it in Preview, then save as draft or publish.')}</DialogDescription>
          </DialogHeader>
          {editing && <SimulationForm key={editing.id || 'new'} initial={editing} categories={cats} onSave={save} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimulationsManager;
